import api from './baseAPI';

export interface LessonLog {
  id: number;
  studentId: number;
  teacherId: number;
  lessonDate: string;
  lessonTime?: string;
  song?: string;
  songYoutubeUrl?: string;
  content?: string;
  homework?: string;
  teacherNote?: string;
  status: 'draft' | 'published';
  isReadByStudent?: boolean;
  createdAt: string;
  updatedAt: string;
  student?: { id: number; name: string };
  teacher?: { id: number; name: string };
  comments?: LessonLogComment[];
}

export interface LessonLogComment {
  id: number;
  lessonLogId: number;
  authorType: 'student' | 'teacher';
  studentId?: number;
  teacherId?: number;
  message: string;
  isReadByTeacher?: boolean;
  isReadByStudent?: boolean;
  createdAt: string;
  student?: { id: number; name: string };
  teacher?: { id: number; name: string };
}

export interface LessonLogInput {
  studentId: number;
  lessonDate: string;
  lessonTime?: string;
  song?: string;
  songYoutubeUrl?: string;
  content?: string;
  homework?: string;
  teacherNote?: string;
  scheduleId?: number;
  status?: 'draft' | 'published';
  logId?: number;  // 수정 시 기존 기록 ID
}

// 선생님용 API
export const lessonLogAPI = {
  // 수업 기록 생성/수정
  save: async (data: LessonLogInput) => {
    const response = await api.post('/lesson-logs', data);
    return response.data;
  },

  // 수업 기록 발행
  publish: async (id: number) => {
    const response = await api.patch(`/lesson-logs/${id}/publish`);
    return response.data;
  },

  // 특정 학생의 수업 기록 목록
  getByStudent: async (studentId: number, params?: { limit?: number; offset?: number }) => {
    const response = await api.get(`/lesson-logs/student/${studentId}`, { params });
    return response.data;
  },

  // 특정 날짜의 수업 기록 (작성 페이지용)
  getByDate: async (studentId: number, date: string) => {
    const response = await api.get('/lesson-logs/by-date', {
      params: { studentId, date }
    });
    return response.data;
  },

  // 미작성 수업 목록
  getUnwritten: async () => {
    const response = await api.get('/lesson-logs/unwritten');
    return response.data;
  },

  // 내 수업 기록 (학생용)
  getMyLogs: async (params?: { limit?: number; offset?: number }) => {
    const response = await api.get('/lesson-logs/my', { params });
    return response.data;
  },

  // 댓글 작성
  addComment: async (logId: number, message: string) => {
    const response = await api.post(`/lesson-logs/${logId}/comments`, { message });
    return response.data;
  },

  // 수업 기록 삭제
  delete: async (logId: number) => {
    const response = await api.delete(`/lesson-logs/${logId}`);
    return response.data;
  },

  // 댓글 삭제
  deleteComment: async (commentId: number) => {
    const response = await api.delete(`/lesson-logs/comments/${commentId}`);
    return response.data;
  },

  // 댓글 수정
  updateComment: async (commentId: number, message: string) => {
    const response = await api.patch(`/lesson-logs/comments/${commentId}`, { message });
    return response.data;
  },

  // 학생별 읽지 않은 댓글 수 조회
  getUnreadCounts: async (): Promise<{ success: boolean; data: Record<number, number> }> => {
    const response = await api.get('/lesson-logs/unread-counts');
    return response.data;
  },

  // 댓글 읽음 처리
  markCommentAsRead: async (commentId: number) => {
    const response = await api.patch(`/lesson-logs/comments/${commentId}/read`);
    return response.data;
  },

  // 수업 기록의 모든 학생 댓글 읽음 처리 (선생님용)
  markLogAsRead: async (logId: number) => {
    const response = await api.patch(`/lesson-logs/${logId}/mark-read`);
    return response.data;
  },

  // 학생용 읽지 않은 수업 기록 + 선생님 댓글 수 조회
  getMyUnreadCount: async (): Promise<{ success: boolean; data: { total: number; logs: number; comments: number } }> => {
    const response = await api.get('/lesson-logs/my/unread-count');
    return response.data;
  },

  // 학생이 수업 기록 확인 시 읽음 처리
  markMyLogAsRead: async (logId: number) => {
    const response = await api.patch(`/lesson-logs/my/${logId}/mark-read`);
    return response.data;
  }
};

// 유튜브 링크에서 썸네일 URL 추출
export const getYoutubeThumbnail = (url: string): string | null => {
  if (!url) return null;

  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/,
    /youtube\.com\/shorts\/([^&?/]+)/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
    }
  }

  return null;
};

// 유튜브 링크에서 비디오 ID 추출
export const getYoutubeVideoId = (url: string): string | null => {
  if (!url) return null;

  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/,
    /youtube\.com\/shorts\/([^&?/]+)/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
};
